// TIBECO - Rapoarte Financiare - Analize și rapoarte complete pentru business ferestre și uși
import React, { useState } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  PieChart, 
  Calendar,
  Download,
  Filter,
  Eye,
  FileText,
  DollarSign,
  Target,
  TrendingDown,
  Activity,
  CreditCard,
  Package,
  Users,
  Building
} from 'lucide-react';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const RapoarteFinanciare = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('2024');
  const [selectedReport, setSelectedReport] = useState('profitLoss');
  const [showReportModal, setShowReportModal] = useState(false);
  const [modalReportType, setModalReportType] = useState('');

  // Date financiare TIBECO pentru 2024
  const financialData = {
    summary: {
      venituri: 1247500,
      cheltuieli: 896300,
      profitBrut: 351200,
      profitNet: 278450,
      tva: 236825,
      cashFlow: 425600
    },
    venitiriLunare: [
      { luna: 'Ian', venituri: 78500, cheltuieli: 58200, profit: 20300 },
      { luna: 'Feb', venituri: 89200, cheltuieli: 67800, profit: 21400 },
      { luna: 'Mar', venituri: 125600, cheltuieli: 84300, profit: 41300 },
      { luna: 'Apr', venituri: 145800, cheltuieli: 96700, profit: 49100 },
      { luna: 'Mai', venituri: 156200, cheltuieli: 102500, profit: 53700 },
      { luna: 'Iun', venituri: 187300, cheltuieli: 124800, profit: 62500 },
      { luna: 'Iul', venituri: 167800, cheltuieli: 118900, profit: 48900 },
      { luna: 'Aug', venituri: 142300, cheltuieli: 98600, profit: 43700 },
      { luna: 'Sep', venituri: 154500, cheltuieli: 145400, profit: -9100 }
    ],
    venitiriCategorie: [
      { 
        categorie: 'Ferestre PVC', 
        venituri: 658750, 
        procent: 52.8,
        comenzi: 156,
        pretMediu: 4225
      },
      { 
        categorie: 'Uși PVC', 
        venituri: 324250, 
        procent: 26.0,
        comenzi: 87,
        pretMediu: 3728
      },
      { 
        categorie: 'Ferestre Aluminiu', 
        venituri: 162750, 
        procent: 13.1,
        comenzi: 34,
        pretMediu: 4787
      },
      { 
        categorie: 'Uși Blindate', 
        venituri: 86875, 
        procent: 6.9,
        comenzi: 23,
        pretMediu: 3777
      },
      { 
        categorie: 'Servicii Montaj', 
        venituri: 14875, 
        procent: 1.2,
        comenzi: 67,
        pretMediu: 222
      }
    ],
    cheltuieliCategorie: [
      { 
        categorie: 'Materiale și Componente',
        suma: 456780,
        procent: 51.0,
        descriere: 'Profile PVC/Aluminiu, geam, feronerie'
      },
      {
        categorie: 'Salarii și Beneficii',
        suma: 198650,
        procent: 22.2,
        descriere: 'Echipa de 11 angajați + contribuții'
      },
      {
        categorie: 'Transport și Logistică',
        suma: 87420,
        procent: 9.8,
        descriere: 'Transport materiale, livări la clienți'
      },
      {
        categorie: 'Utilități și Chirie',
        suma: 65890,
        procent: 7.3,
        descriere: 'Atelier, depozit, birouri'
      },
      {
        categorie: 'Marketing și Publicitate',
        suma: 42670,
        procent: 4.8,
        descriere: 'Website, Facebook Ads, materiale promoționale'
      },
      {
        categorie: 'Alte Cheltuieli',
        suma: 44890,
        procent: 4.9,
        descriere: 'Asigurări, consultanță, întreținere echipamente'
      }
    ],
    kpiTrend: {
      marjaProfitBrut: { current: 28.2, previous: 25.8, trend: 'up' },
      marjaProfitNet: { current: 22.3, previous: 19.7, trend: 'up' },
      rataCreștere: { current: 18.5, previous: 12.3, trend: 'up' },
      durataMedieColectare: { current: 28, previous: 35, trend: 'down' },
      costMediumPerComanda: { current: 2987, previous: 3245, trend: 'down' }
    },
    clientiTop: [
      { client: 'SC Constructor Alpha SRL', venituri: 89400, comenzi: 8, tipClient: 'B2B' },
      { client: 'Vila Primăverii SRL', venituri: 67800, comenzi: 4, tipClient: 'B2B' },
      { client: 'Hotel Mountain View', venituri: 45600, comenzi: 3, tipClient: 'B2B' },
      { client: 'Popescu Maria', venituri: 31200, comenzi: 6, tipClient: 'B2C' },
      { client: 'Georgescu Ion', venituri: 28900, comenzi: 4, tipClient: 'B2C' }
    ]
  };

  // Configurări rapoarte disponibile
  const availableReports = [
    {
      id: 'profitLoss',
      title: 'Profit & Loss (P&L)',
      description: 'Situația veniturilor și cheltuielilor pentru perioada selectată',
      icon: TrendingUp,
      frequency: 'Lunar/Trimestrial',
      lastGenerated: '2024-09-30'
    },
    {
      id: 'balanceSheet',
      title: 'Bilanț Contabil',
      description: 'Active, pasive și capitaluri proprii TIBECO',
      icon: FileText,
      frequency: 'Trimestrial',
      lastGenerated: '2024-09-30'
    },
    {
      id: 'cashFlow',
      title: 'Cash Flow Statement',
      description: 'Fluxurile de numerar din activități operaționale',
      icon: CreditCard,
      frequency: 'Lunar',
      lastGenerated: '2024-09-30'
    },
    {
      id: 'salesAnalysis',
      title: 'Analiză Vânzări',
      description: 'Performanța vânzărilor pe produse și clienți',
      icon: BarChart3,
      frequency: 'Lunar',
      lastGenerated: '2024-09-30'
    },
    {
      id: 'taxReport',
      title: 'Raport TVA',
      description: 'Declarația TVA cu detalii pe factturi emise/primite',
      icon: Building,
      frequency: 'Lunar',
      lastGenerated: '2024-09-30'
    },
    {
      id: 'employeeCosts',
      title: 'Costuri Angajați',
      description: 'Analiză completă costuri cu personalul',
      icon: Users,
      frequency: 'Lunar',
      lastGenerated: '2024-09-30'
    }
  ];

  const handleGenerateReport = (reportType) => {
    setModalReportType(reportType);
    setShowReportModal(true);
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? <TrendingUp className="w-4 h-4 text-green-500" /> : 
           trend === 'down' ? <TrendingDown className="w-4 h-4 text-red-500" /> :
           <Activity className="w-4 h-4 text-gray-500" />;
  };

  const getTrendColor = (trend) => {
    return trend === 'up' ? 'text-green-600' : 
           trend === 'down' ? 'text-red-600' : 'text-gray-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Rapoarte Financiare TIBECO
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Analize financiare complete pentru business-ul de ferestre și uși
          </p>
        </div>
        
        <div className="flex gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="form-input"
          >
            <option value="2024">Anul 2024</option>
            <option value="2023">Anul 2023</option>
            <option value="q3-2024">Q3 2024</option>
            <option value="q2-2024">Q2 2024</option>
            <option value="septembrie-2024">Septembrie 2024</option>
          </select>
          
          <button className="btn-primary flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Complet
          </button>
        </div>
      </div>

      {/* KPI-uri Principale */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Venituri Totale</p>
              <p className="text-2xl font-bold text-green-600">
                {financialData.summary.venituri.toLocaleString('ro-RO')} RON
              </p>
              <div className="flex items-center gap-1 mt-1">
                {getTrendIcon('up')}
                <span className="text-sm text-green-600">+18.5% vs 2023</span>
              </div>
            </div>
            <TrendingUp className="w-8 h-8 text-green-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Cheltuieli Totale</p>
              <p className="text-2xl font-bold text-red-600">
                {financialData.summary.cheltuieli.toLocaleString('ro-RO')} RON
              </p>
              <div className="flex items-center gap-1 mt-1">
                {getTrendIcon('up')}
                <span className="text-sm text-red-600">+12.3% vs 2023</span>
              </div>
            </div>
            <BarChart3 className="w-8 h-8 text-red-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Profit Net</p>
              <p className="text-2xl font-bold text-primary-600">
                {financialData.summary.profitNet.toLocaleString('ro-RO')} RON
              </p>
              <div className="flex items-center gap-1 mt-1">
                {getTrendIcon('up')}
                <span className="text-sm text-green-600">Marjă {financialData.kpiTrend.marjaProfitNet.current}%</span>
              </div>
            </div>
            <Target className="w-8 h-8 text-primary-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Cash Flow</p>
              <p className="text-2xl font-bold text-blue-600">
                {financialData.summary.cashFlow.toLocaleString('ro-RO')} RON
              </p>
              <div className="flex items-center gap-1 mt-1">
                {getTrendIcon('up')}
                <span className="text-sm text-blue-600">Pozitiv 9 luni</span>
              </div>
            </div>
            <CreditCard className="w-8 h-8 text-blue-600" />
          </div>
        </Card>
      </div>

      {/* KPI-uri Tendințe */}
      <Card title="Indicatori Cheie de Performanță" icon={Activity}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {Object.entries(financialData.kpiTrend).map(([key, kpi]) => (
            <div key={key} className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                {getTrendIcon(kpi.trend)}
                <span className={`text-2xl font-bold ${getTrendColor(kpi.trend)}`}>
                  {kpi.current}{key.includes('Marja') || key.includes('rata') ? '%' : key.includes('cost') ? ' RON' : key.includes('durată') ? ' zile' : ''}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </p>
              <p className="text-xs text-gray-500">
                vs anterior: {kpi.previous}{key.includes('Marja') || key.includes('rata') ? '%' : key.includes('cost') ? ' RON' : key.includes('durată') ? ' zile' : ''}
              </p>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Venituri pe Categorii */}
        <Card title="Venituri pe Categorii Produse" icon={PieChart}>
          <div className="space-y-4">
            {financialData.venitiriCategorie.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {item.categorie}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {item.venituri.toLocaleString('ro-RO')} RON
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500">
                      {item.comenzi} comenzi • Preț mediu: {item.pretMediu.toLocaleString('ro-RO')} RON
                    </span>
                    <span className="text-xs text-gray-500">
                      {item.procent}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${item.procent}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Cheltuieli pe Categorii */}
        <Card title="Distribuție Cheltuieli" icon={BarChart3}>
          <div className="space-y-4">
            {financialData.cheltuieliCategorie.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {item.categorie}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {item.suma.toLocaleString('ro-RO')} RON
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500">
                      {item.descriere}
                    </span>
                    <span className="text-xs text-gray-500">
                      {item.procent}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-red-500 to-orange-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${item.procent}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Top Clienți */}
        <Card title="Top 5 Clienți după Venituri" icon={Users}>
          <div className="space-y-4">
            {financialData.clientiTop.map((client, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full">
                    <span className="text-sm font-bold text-primary-600">{index + 1}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{client.client}</p>
                    <p className="text-xs text-gray-500">{client.comenzi} comenzi • {client.tipClient}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary-600">
                    {client.venituri.toLocaleString('ro-RO')} RON
                  </p>
                  <p className="text-xs text-gray-500">
                    Mediu: {(client.venituri / client.comenzi).toLocaleString('ro-RO')} RON
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Trend Lunar */}
        <Card title="Evoluție Lunară Profit" icon={TrendingUp}>
          <div className="space-y-3">
            {financialData.venitiriLunare.slice(-6).map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{item.luna} 2024</p>
                    <p className="text-xs text-gray-500">
                      V: {item.venituri.toLocaleString('ro-RO')} • C: {item.cheltuieli.toLocaleString('ro-RO')}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${
                    item.profit >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {item.profit >= 0 ? '+' : ''}{item.profit.toLocaleString('ro-RO')} RON
                  </p>
                  <p className="text-xs text-gray-500">
                    Marjă: {((item.profit / item.venituri) * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Rapoarte Disponibile */}
      <Card title="Rapoarte Disponibile" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {availableReports.map((report) => {
            const IconComponent = report.icon;
            return (
              <div key={report.id} className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-xl">
                    <IconComponent className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{report.title}</h3>
                    <p className="text-sm text-gray-500">{report.frequency}</p>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {report.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Ultimul: {new Date(report.lastGenerated).toLocaleDateString('ro-RO')}
                  </span>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleGenerateReport(report.id)}
                      className="btn-sm btn-secondary flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      Vezi
                    </button>
                    <button className="btn-sm btn-primary flex items-center gap-1">
                      <Download className="w-3 h-3" />
                      PDF
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Modal Vizualizare Raport */}
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title={`Raport ${availableReports.find(r => r.id === modalReportType)?.title || ''}`}
        size="lg"
      >
        {modalReportType && (
          <div className="space-y-6">
            {modalReportType === 'profitLoss' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Profit & Loss Statement - TIBECO {selectedPeriod}
                </h3>
                
                <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
                  <div className="space-y-4">
                    <div className="flex justify-between border-b pb-2">
                      <span className="font-semibold text-green-600">VENITURI TOTALE</span>
                      <span className="font-bold text-green-600">
                        {financialData.summary.venituri.toLocaleString('ro-RO')} RON
                      </span>
                    </div>
                    
                    {financialData.venitiriCategorie.map((item, index) => (
                      <div key={index} className="flex justify-between pl-4">
                        <span className="text-gray-600">{item.categorie}</span>
                        <span>{item.venituri.toLocaleString('ro-RO')} RON</span>
                      </div>
                    ))}
                    
                    <div className="flex justify-between border-b pb-2 mt-6">
                      <span className="font-semibold text-red-600">CHELTUIELI TOTALE</span>
                      <span className="font-bold text-red-600">
                        {financialData.summary.cheltuieli.toLocaleString('ro-RO')} RON
                      </span>
                    </div>
                    
                    {financialData.cheltuieliCategorie.map((item, index) => (
                      <div key={index} className="flex justify-between pl-4">
                        <span className="text-gray-600">{item.categorie}</span>
                        <span>{item.suma.toLocaleString('ro-RO')} RON</span>
                      </div>
                    ))}
                    
                    <div className="flex justify-between border-t pt-4 mt-6">
                      <span className="font-bold text-primary-600 text-lg">PROFIT BRUT</span>
                      <span className="font-bold text-primary-600 text-lg">
                        {financialData.summary.profitBrut.toLocaleString('ro-RO')} RON
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">TVA de plată</span>
                      <span>{financialData.summary.tva.toLocaleString('ro-RO')} RON</span>
                    </div>
                    
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-bold text-primary-600 text-xl">PROFIT NET</span>
                      <span className="font-bold text-primary-600 text-xl">
                        {financialData.summary.profitNet.toLocaleString('ro-RO')} RON
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {modalReportType === 'salesAnalysis' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Analiză Detaliată Vânzări TIBECO {selectedPeriod}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">Comenzi Totale</h4>
                    <p className="text-2xl font-bold text-blue-600">
                      {financialData.venitiriCategorie.reduce((sum, cat) => sum + cat.comenzi, 0)}
                    </p>
                  </div>
                  
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800 dark:text-green-200">Valoare Medie</h4>
                    <p className="text-2xl font-bold text-green-600">
                      {Math.round(financialData.summary.venituri / financialData.venitiriCategorie.reduce((sum, cat) => sum + cat.comenzi, 0)).toLocaleString('ro-RO')} RON
                    </p>
                  </div>
                  
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <h4 className="font-semibold text-purple-800 dark:text-purple-200">Produs Top</h4>
                    <p className="text-lg font-bold text-purple-600">Ferestre PVC</p>
                    <p className="text-sm text-purple-700">52.8% din venituri</p>
                  </div>
                </div>
              </div>
            )}
            
            {modalReportType === 'cashFlow' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Cash Flow Statement TIBECO {selectedPeriod}
                </h3>
                
                <div className="space-y-4">
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">Intrări de Numerar</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Încasări de la clienți</span>
                        <span className="font-medium">{(financialData.summary.venituri * 0.92).toLocaleString('ro-RO')} RON</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Alte intrări</span>
                        <span className="font-medium">{(financialData.summary.venituri * 0.08).toLocaleString('ro-RO')} RON</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                    <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">Ieșiri de Numerar</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Plăți furnizori</span>
                        <span className="font-medium">{(financialData.summary.cheltuieli * 0.65).toLocaleString('ro-RO')} RON</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Salarii și contribuții</span>
                        <span className="font-medium">198,650 RON</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Alte cheltuieli</span>
                        <span className="font-medium">{(financialData.summary.cheltuieli * 0.13).toLocaleString('ro-RO')} RON</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg">
                    <div className="flex justify-between">
                      <span className="font-bold text-primary-800 dark:text-primary-200">Cash Flow Net</span>
                      <span className="font-bold text-primary-600 text-xl">
                        +{financialData.summary.cashFlow.toLocaleString('ro-RO')} RON
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {!['profitLoss', 'salesAnalysis', 'cashFlow'].includes(modalReportType) && (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Raport în Dezvoltare
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Acest tip de raport va fi disponibil în versiunile următoare.<br />
                  Momentan puteți accesa rapoartele P&L, Cash Flow și Analiză Vânzări.
                </p>
              </div>
            )}
            
            {/* Acțiuni Modal */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button 
                onClick={() => setShowReportModal(false)}
                className="btn-secondary"
              >
                Închide
              </button>
              {['profitLoss', 'salesAnalysis', 'cashFlow'].includes(modalReportType) && (
                <>
                  <button className="btn-secondary flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Export Excel
                  </button>
                  <button className="btn-primary flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Generează PDF
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RapoarteFinanciare;